import React, { Dispatch, useEffect, useState } from 'react';
import {
  Card,
  CardBody,
  CardActions,
  CardHeader,
} from '@progress/kendo-react-layout';
import {
  Checkbox,
  TextArea,
  TextAreaChangeEvent,
  TextBox,
} from '@progress/kendo-react-inputs';
import { AutoComplete, DropDownList } from '@progress/kendo-react-dropdowns';
import { Button } from '@progress/kendo-react-buttons';
import { plusIcon } from '@progress/kendo-svg-icons';
import { SvgIcon } from '@progress/kendo-react-common';
import styles from './_portal-references.module.scss';
import { CreateUpdateReference, Reference, emptyReference } from '../../Models/Reference';
import { State } from '../../Models/State';
import { useSelector } from 'react-redux';
import { AppState } from '../../Store/rootReducer';
import { loadCustomerReferenceRelationships, loadStates } from '../../Store/Shared/actions';
import { getReferenceRelationshipForLanguage, getWordingForLanguage } from '../../Helpers/LanguageDictionary';
import { CustomerPortalUser } from '../../Models/CustomerPortalUser';
import { createReference, deleteReference, loadReferences, updateReference } from '../../Store/CustomerPortal/actions';
import { isGuid } from '../../Helpers/Utilities';
import { CustomerReferenceRelationship } from '../../Models/CustomerReferenceRelationship';
import { PhoneInput, formatPhoneNumber, hasValue, removeSpecialCharactersFromPhoneNumber } from '@amsanalytics/react-shared-library';
import { isNotNil } from 'ramda';
import classNames from 'classnames';

export interface PortalReferencesProps {
  dispatch: Dispatch<any>;
  canAddReferences: boolean; // whether or not the user can add references
  canEditReferences: boolean; // whether or not the user can edit references
  selectedLanguageId: string;
  user: CustomerPortalUser;
  width: number;
  selectedApplicationCustomerId: string;
}

export const PortalReferences = (props: PortalReferencesProps) => {
  // references from redux store
  const references: Reference[] = useSelector((appState: AppState) => appState.customerPortal.references);
  // states from redux store
  const states: State[] = useSelector((appState: AppState) => appState.shared.states);
  const [referenceIdToConfirmRemoval, setReferenceIdToConfirmRemoval] = useState<string>();
  // list of reference object that is in "edit" mode
  const [referenceEditItem, setReferenceEditItem] = useState<Reference>();
  // customer reference relationships
  const customerReferenceRelationships: CustomerReferenceRelationship[] = useSelector((appState: AppState) => appState.shared.customerReferenceRelationships);

  const customerId = props.user.applicationCustomers.find(c => c.id === props.selectedApplicationCustomerId)!.customerId;
  // load references, relationships, and states on component load
  useEffect(() => {
    props.dispatch(loadReferences(customerId, props.user.customerPortalLinkId));
    props.dispatch(loadStates(props.user.customerPortalLinkId));
    props.dispatch(loadCustomerReferenceRelationships(props.user.customerPortalLinkId));
  }, []);

  // user clicks "add reference" button
  const onAddReference = () => {
    // create a new empty reference and set the newly-added reference to "edit" mode
    setReferenceEditItem(
      { ...emptyReference, id: references.length.toString(), modifiedDate: new Date() }
    );
  }

  // returns a sorted list of customer reference relationships in the user's selected language. removes dupes based on name
  const getReferenceRelationshipsForLanguage = () => {
    return customerReferenceRelationships.filter(r => isNotNil(r.id) && isNotNil(r.name) && hasValue(r.name))
      .map((r) => {
        return {
          id: r.id,
          name: getReferenceRelationshipForLanguage(r.name.toLowerCase(), props.selectedLanguageId)
        };
      }).sort((a, b) => a.name > b.name ? 1 : -1);
  }

  // whether or not the user can save the reference. Validation requirements are:
  // 1. Name
  // 2. Relationship
  // 3. Cell phone with parentheses and dashes stripped
  const canSaveReference = (reference: Reference) => {
    return hasValue(reference.name)
      && isNotNil(reference.customerReferenceRelationshipId)
      && hasValue(reference.customerReferenceRelationshipId)
      && (hasValue(reference.cellPhone) && reference.cellPhone.replace(/[^0-9]/g, '').length === 10);
  }

  return (
    <div className={styles.uploadReferencesContainer}>
      <h3 className={styles.referenceHeader}>
        {props.selectedLanguageId === '1' ? 'References' : 'Referencias'}
        {
          props.canAddReferences && (
            <Button
              style={{ background: '#ebebeb !important' }}
              fillMode="outline"
              className={styles.addReferenceBtn}
              disabled={isNotNil(referenceEditItem)}
              onClick={() => {
                onAddReference();
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <SvgIcon
                  icon={plusIcon}
                  size="medium"
                  style={{ marginRight: '3px' }}
                />
                {getWordingForLanguage('addReference', props.selectedLanguageId)}
              </div>
            </Button>
          )
        }
      </h3>
      {props.user.minimumNumberOfReferences > 0 && references.length < props.user.minimumNumberOfReferences && (
        <div className={styles.minimumNumberOfReferences}>
          {getWordingForLanguage('minimumReferencesMessage', props.selectedLanguageId)}{props.user.minimumNumberOfReferences - (references.length || 0)}{getWordingForLanguage('minimumReferencesMessageContinued', props.selectedLanguageId)}
        </div>
      )}
      {[...references.filter(r => r.id !== referenceEditItem?.id), ...(isNotNil(referenceEditItem) ? [referenceEditItem] : [])]
        .sort((a: Reference, b: Reference) => {
          return a.modifiedDate > b.modifiedDate ? 1 : -1;
        })
        .map((reference: Reference, index: number) => {
          return referenceEditItem?.id === reference.id ? (
            <div key={index} style={{ maxWidth: '500px' }}>
              <Card className={styles.cardContainer}>
                <CardBody>
                  <div className={props.width < 600 ? styles.cardBodyMobile : styles.cardBody}>
                    <div className={props.width > 600 ? styles.inputCol : styles.inputColFullWidth}>
                      <div>{getWordingForLanguage('name', props.selectedLanguageId)}*</div>
                      <TextBox
                        value={reference.name}
                        onChange={(e) => {
                          setReferenceEditItem({ ...reference, name: e.value as string });
                        }}
                        placeholder={`${getWordingForLanguage('name', props.selectedLanguageId)}...`}
                        fillMode="outline"
                      />
                    </div>
                    <div className={props.width > 600 ? classNames(styles.inputCol, styles.dropdown) : styles.inputColFullWidth}>
                      <div>{getWordingForLanguage('relationship', props.selectedLanguageId)}*</div>
                      <DropDownList
                        fillMode="outline"
                        data={getReferenceRelationshipsForLanguage()}
                        value={getReferenceRelationshipsForLanguage().find(r => r.id.toLowerCase() === reference.customerReferenceRelationshipId?.toLowerCase())}
                        dataItemKey={'id'}
                        textField={'name'}
                        onChange={(e) => {
                          setReferenceEditItem({ ...reference, customerReferenceRelationshipId: e.value.id as string });
                        }}
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div className={props.width > 600 ? styles.inputCol : styles.inputColFullWidth}>
                      <div>{getWordingForLanguage('email', props.selectedLanguageId)}</div>
                      <TextBox
                        value={reference.emailAddress}
                        onChange={(e) => {
                          setReferenceEditItem({ ...reference, emailAddress: e.value as string });
                        }}
                        placeholder={`${getWordingForLanguage('email', props.selectedLanguageId)}...`}
                        fillMode="outline"
                      />
                    </div>
                    <div className={props.width > 600 ? styles.inputCol : styles.inputColFullWidth}>
                      <PhoneInput
                        value={reference.homePhone}
                        onChangeValue={(e: string) => {
                          setReferenceEditItem({ ...reference, homePhone: e as string });
                        }}
                        fillMode="outline"
                        label={getWordingForLanguage('homePhone', props.selectedLanguageId)}
                      />
                    </div>
                    <div className={props.width > 600 ? styles.inputCol : styles.inputColFullWidth}>
                      <PhoneInput
                        value={reference.cellPhone}
                        onChangeValue={(e: string) => {
                          // strip underscores from phone number
                          setReferenceEditItem({ ...reference, cellPhone: e.replace(/_/g, '') });
                        }}
                        fillMode="outline"
                        label={`${getWordingForLanguage('cellPhone', props.selectedLanguageId)}*`}
                      />
                    </div>
                    <div className={props.width > 600 ? styles.inputCol : styles.inputColFullWidth}>
                      <div>{getWordingForLanguage('address', props.selectedLanguageId)}</div>
                      <TextBox
                        value={reference.address}
                        onChange={(e) => {
                          setReferenceEditItem({ ...reference, address: e.value as string });
                        }}
                        placeholder={`${getWordingForLanguage('address', props.selectedLanguageId)}...`}
                        fillMode="outline"
                      />
                    </div>
                    <div className={props.width > 600 ? styles.inputCol : styles.inputColFullWidth}>
                      <div>{getWordingForLanguage('city', props.selectedLanguageId)}</div>
                      <TextBox
                        value={reference.city}
                        onChange={(e) => {
                          setReferenceEditItem({ ...reference, city: e.value as string });
                        }}
                        placeholder={`${getWordingForLanguage('city', props.selectedLanguageId)}...`}
                        fillMode="outline"
                      />
                    </div>
                    <div className={props.width > 600 ? styles.inputCol : styles.inputColFullWidth}>
                      <div>{getWordingForLanguage('state', props.selectedLanguageId)}</div>
                      <AutoComplete
                        fillMode="outline"
                        data={states}
                        value={states.find(s => s.id === reference.stateId)?.name}
                        dataItemKey={'id'}
                        textField={'name'}
                        onChange={(e) => {
                          setReferenceEditItem({ ...reference, stateId: e.value as string });
                        }}
                        placeholder={`${getWordingForLanguage('state', props.selectedLanguageId)}...`}
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div className={props.width > 600 ? styles.inputCol : styles.inputColFullWidth}>
                      <div>
                        <div>{getWordingForLanguage('zipCode', props.selectedLanguageId)}</div>
                        <TextBox
                          value={reference.zip}
                          type='number'
                          onChange={(e) => {
                            setReferenceEditItem({ ...reference, zip: e.value as string });
                          }}
                          placeholder={`${getWordingForLanguage('zipCode', props.selectedLanguageId)}...`}
                          fillMode="outline"
                        />
                      </div>
                    </div>
                    <div className={props.width > 600 ? styles.inputCol : styles.inputColFullWidth} style={{ gap: 7, justifyContent: 'center' }}>
                      <Checkbox
                        label={getWordingForLanguage('liveswithcustomerquestion', props.selectedLanguageId)}
                        checked={reference.livesWith}
                        onChange={(e) => {
                          setReferenceEditItem({ ...reference, livesWith: e.value });
                        }}
                      />
                      <Checkbox
                        label={getWordingForLanguage('callable', props.selectedLanguageId)}
                        checked={reference.callable}
                        onChange={(e) => {
                          setReferenceEditItem({ ...reference, callable: e.value });
                        }}
                      />
                    </div>
                    <div className={props.width > 600 ? styles.inputCol : styles.inputColFullWidth}>
                      <div>{getWordingForLanguage('notes', props.selectedLanguageId)}</div>
                      <TextArea
                        value={reference.note}
                        rows={2}
                        onChange={(e: TextAreaChangeEvent) => {
                          setReferenceEditItem({ ...reference, note: e.value as string });
                        }}
                        placeholder={`${getWordingForLanguage('notes', props.selectedLanguageId)}...`}
                        fillMode="outline"
                      />
                    </div>
                  </div>
                </CardBody>
                <CardActions style={{ margin: 0, padding: '0px 1em 5px 1em' }}>
                  {referenceIdToConfirmRemoval !== reference.id && (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ textAlign: 'left' }}>{getWordingForLanguage('requiredFields', props.selectedLanguageId)}</span>
                      <div>
                        <Button
                          themeColor={'primary'}
                          disabled={!canSaveReference(reference)}
                          fillMode="flat"
                          style={{
                            alignSelf: 'flex-start',
                            textTransform: 'none',
                            width: 'fit-content',
                          }}
                          onClick={() => {
                            isGuid(reference.id)
                              ? props.dispatch(updateReference(
                                props.user.customerPortalLinkId,
                                {
                                  ...reference,
                                  customerId: customerId,
                                  stateId: states.find(s => s.name === reference.stateId)?.id ?? reference.stateId,
                                  homePhone: removeSpecialCharactersFromPhoneNumber(reference.homePhone),
                                  cellPhone: removeSpecialCharactersFromPhoneNumber(reference.cellPhone),
                                  customerReferenceRelationshipId: (reference.customerReferenceRelationshipId as any)?.id ?? reference.customerReferenceRelationshipId,
                                } as CreateUpdateReference))
                              : props.dispatch(createReference(
                                customerId,
                                props.user.customerPortalLinkId,
                                {
                                  ...reference,
                                  id: undefined,
                                  customerId: customerId,
                                  stateId: states.find(s => s.name === reference.stateId)?.id,
                                  customerReferenceRelationshipId: (reference.customerReferenceRelationshipId as any)?.id ?? reference.customerReferenceRelationshipId,
                                  homePhone: removeSpecialCharactersFromPhoneNumber(reference.homePhone),
                                  cellPhone: removeSpecialCharactersFromPhoneNumber(reference.cellPhone)
                                } as CreateUpdateReference));
                            setReferenceEditItem(undefined);
                            setReferenceIdToConfirmRemoval(undefined);
                          }}
                        >
                          {isGuid(reference.id) ? getWordingForLanguage('save', props.selectedLanguageId) : getWordingForLanguage('create', props.selectedLanguageId)}
                        </Button>
                        <Button
                          themeColor={'error'}
                          fillMode="flat"
                          style={{
                            alignSelf: 'flex-start',
                            textTransform: 'none',
                            width: 'fit-content',
                          }}
                          onClick={() => {
                            setReferenceEditItem(undefined);
                          }}
                        >
                          {getWordingForLanguage('cancel', props.selectedLanguageId)}
                        </Button>
                      </div>
                    </div>
                  )}
                  {referenceIdToConfirmRemoval === reference.id && (
                    <div>
                      {getWordingForLanguage('confirmRemoval', props.selectedLanguageId)}
                      &nbsp;&nbsp;
                      <Button
                        themeColor={'error'}
                        fillMode="flat"
                        style={{
                          alignSelf: 'flex-start',
                          textTransform: 'none',
                          width: 'fit-content',
                        }}
                        onClick={() => {
                          if (isGuid(reference.id)) {
                            props.dispatch(deleteReference(props.selectedApplicationCustomerId, props.user.customerPortalLinkId, reference.id));
                          } else {
                            setReferenceEditItem(undefined);
                          }
                          setReferenceIdToConfirmRemoval(undefined);
                        }}
                      >
                        {getWordingForLanguage('yes', props.selectedLanguageId)}
                      </Button>
                      <Button
                        themeColor={'base'}
                        fillMode="flat"
                        style={{
                          alignSelf: 'flex-start',
                          textTransform: 'none',
                          width: 'fit-content',
                        }}
                        onClick={() =>
                          setReferenceIdToConfirmRemoval(undefined)
                        }
                      >
                        No
                      </Button>
                    </div>
                  )}
                </CardActions>
              </Card>
            </div>
          ) : (
            <div key={index} className={styles.summaryCardcontainer}>
              <Card>
                <CardHeader className={styles.summaryHeader}>
                  {reference.name}&nbsp;
                  {customerReferenceRelationships.find((r) => r.id === reference.customerReferenceRelationshipId) && (
                    <span>
                      &bull;&nbsp;
                      {customerReferenceRelationships.find((r) => r.id === reference.customerReferenceRelationshipId)?.name && getReferenceRelationshipForLanguage(customerReferenceRelationships.find((r) => r.id === reference.customerReferenceRelationshipId)!.name.toLowerCase(), props.selectedLanguageId)}
                    </span>
                  )}
                </CardHeader>
                <CardBody className={styles.summaryBody}>
                  {reference.emailAddress && (
                    <div>
                      {getWordingForLanguage('email', props.selectedLanguageId)}
                      :&nbsp;{reference.emailAddress}
                    </div>
                  )}
                  {reference.homePhone && (
                    <div>
                      {getWordingForLanguage('homePhone', props.selectedLanguageId)}
                      : {formatPhoneNumber(reference.homePhone)}
                    </div>
                  )}
                  {reference.cellPhone && (
                    <div>
                      {getWordingForLanguage('cellPhone', props.selectedLanguageId)}
                      : {formatPhoneNumber(reference.cellPhone)}
                    </div>
                  )}
                  <div>
                    {reference.address}&nbsp;{reference.city}
                    {reference.stateId && (
                      <span>
                        ,&nbsp;
                        {states.find((s) => s.id === reference.stateId)?.name}
                      </span>
                    )}
                    &nbsp;{reference.zip}
                  </div>
                  <div>
                    {getWordingForLanguage('liveswithcustomer', props.selectedLanguageId)}:&nbsp;
                    {reference.livesWith ? getWordingForLanguage('yes', props.selectedLanguageId) : getWordingForLanguage('no', props.selectedLanguageId)}
                  </div>
                  <div>
                    {getWordingForLanguage('callable', props.selectedLanguageId)}
                    &nbsp;{reference.callable ? getWordingForLanguage('yes', props.selectedLanguageId) : getWordingForLanguage('no', props.selectedLanguageId)}
                  </div>
                  {reference.note && reference.note.length > 0 && (
                    <div>{getWordingForLanguage('notes', props.selectedLanguageId)}:&nbsp;{reference.note}</div>
                  )}
                </CardBody>
                <CardActions className={styles.summaryActions}>
                  {referenceIdToConfirmRemoval !== reference.id && props.canEditReferences && (
                    <div>
                      <Button
                        themeColor={'primary'}
                        fillMode="flat"
                        disabled={isNotNil(referenceEditItem)}
                        style={{
                          alignSelf: 'flex-start',
                          textTransform: 'none',
                          width: 'fit-content',
                        }}
                        onClick={() => {
                          setReferenceEditItem({ ...reference });
                        }}
                      >
                        {getWordingForLanguage('edit', props.selectedLanguageId)}
                      </Button>
                      <Button
                        themeColor={'error'}
                        fillMode="flat"
                        style={{
                          alignSelf: 'flex-start',
                          textTransform: 'none',
                          width: 'fit-content',
                        }}
                        onClick={() =>
                          setReferenceIdToConfirmRemoval(reference.id)
                        }
                      >
                        {getWordingForLanguage('remove', props.selectedLanguageId)}
                      </Button>
                    </div>
                  )}
                  {referenceIdToConfirmRemoval === reference.id && (
                    <div>
                      {getWordingForLanguage('confirmRemoval', props.selectedLanguageId)}
                      &nbsp;&nbsp;
                      <Button
                        themeColor={'error'}
                        fillMode="flat"
                        style={{
                          alignSelf: 'flex-start',
                          textTransform: 'none',
                          width: 'fit-content',
                        }}
                        onClick={() => {
                          if (isGuid(reference.id)) {
                            props.dispatch(deleteReference(props.selectedApplicationCustomerId, props.user.customerPortalLinkId, reference.id));
                          } else {
                            setReferenceEditItem(undefined);
                          }
                          setReferenceIdToConfirmRemoval(undefined);
                        }}
                      >
                        {getWordingForLanguage('yes', props.selectedLanguageId)}
                      </Button>
                      <Button
                        themeColor={'base'}
                        fillMode="flat"
                        style={{
                          alignSelf: 'flex-start',
                          textTransform: 'none',
                          width: 'fit-content',
                        }}
                        onClick={() =>
                          setReferenceIdToConfirmRemoval(undefined)
                        }
                      >
                        No
                      </Button>
                    </div>
                  )}
                </CardActions>
              </Card>
            </div>
          );
        })}
    </div >
  );
};
